import React, { useState, useMemo } from "react";
import KfsArticlesTable from "./KfsArticlesTable";
import ReportArticleModal from "./ReportArticleModal";
import { reprocessKfsArticle, reportKfsArticle } from "../../services/api";

import LabelManagementPanel from "./labels/LabelManagementPanel";
import LabelFilter from "./labels/LabelFilter";
import { useLabels } from "./labels/useLabels";

interface ArticleListItem {
  id: string;
  title: string;
  publicationDate: string;
  excerpt: string;
  tags: string[];
  hasKfsFlag: boolean;
  url: string;
  office: {
    district: string;
    name: string;
    provinceName: string;
    province: string;
    locality: string;
  };
}

interface ArticlesListProps {
  articles: ArticleListItem[];
  total: number;
  onProvinceClick?: (province: string) => void;
  pagination?: {
    currentPage: number;
    totalPages: number;
    limit: number;
    total: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  onPageChange?: (newPage: number) => void;
  onLimitChange?: (newLimit: number) => void;
  userRole?: string;
}

const ArticlesList: React.FC<ArticlesListProps> = ({
  articles,
  total,
  onProvinceClick,
  pagination,
  onPageChange,
  onLimitChange,
  userRole,
}) => {
  const [sortConfig, setSortConfig] = useState<{
    column: "publicationDate";
    order: "ASC" | "DESC";
  } | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportingArticle, setReportingArticle] = useState<{
    id: string | number;
    title: string;
    type: "kfs" | "bur";
  } | null>(null);
  const [labelFilterMode, setLabelFilterMode] = useState<"all" | "labeled" | string>("all");

  const articleIds = useMemo(
    () => articles.map((a) => a.id),
    [articles]
  );

  const {
    labels,
    articleLabels,
    labelsLoading,
    error: labelsError,
    createLabel,
    updateLabel,
    deleteLabel,
    setArticleLabel,
  } = useLabels({ articleType: "kfs", articleIds });

  const [labelsErrorDismissed, setLabelsErrorDismissed] = useState(false);

  const filteredArticles = useMemo(() => {
    if (labelFilterMode === "all") return articles;
    if (labelFilterMode === "labeled") {
      return articles.filter((a) => articleLabels[a.id]);
    }
    // Filter by specific label
    return articles.filter(
      (a) => articleLabels[a.id]?.labelId === labelFilterMode
    );
  }, [articles, articleLabels, labelFilterMode]);

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    setSortConfig({
      column: sortBy as "publicationDate",
      order: sortOrder as "ASC" | "DESC",
    });
  };

  const handleReprocess = async (articleId: string | number) => {
    try {
      await reprocessKfsArticle(articleId as string, {
        skipNotifications: true,
        forceReprocess: true,
      });
      setToastType("success");
      setToastMessage(
        `Artykuł ${articleId} został wysłany do ponownego przetwarzania`
      );
      setTimeout(() => setToastMessage(null), 3000);
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || error.message || "Wystąpił błąd";
      setToastType("error");
      setToastMessage(`Błąd: ${errorMsg}`);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  const handleReport = (articleId: string | number, type: "kfs" | "bur") => {
    const article = articles.find((a) => a.id === articleId);
    if (article) {
      setReportingArticle({
        id: articleId,
        title: article.title,
        type,
      });
      setReportModalOpen(true);
    }
  };

  const handleReportSubmit = async (reason?: string) => {
    if (!reportingArticle) return;

    try {
      await reportKfsArticle(reportingArticle.id as string, reason);
      setToastType("success");
      setToastMessage("Dziękujemy za zgłoszenie! Zajmiemy się tym problemem.");
      setTimeout(() => setToastMessage(null), 5000);
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || error.message || "Wystąpił błąd";
      setToastType("error");
      setToastMessage(`Błąd: ${errorMsg}`);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  if (articles.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-neutral-400 mb-2">
          <svg
            className="mx-auto h-12 w-12"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-neutral-900">Brak artykułów</h3>
        <p className="text-sm text-neutral-500">
          Nie znaleziono artykułów spełniających wybrane kryteria.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Toast notification */}
      {toastMessage && (
        <div
          className={`fixed top-4 right-4 px-4 py-2 rounded-lg shadow-lg z-50 max-w-sm ${
            toastType === "success"
              ? "bg-green-600 text-white"
              : "bg-red-600 text-white"
          }`}
        >
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              {toastType === "success" ? (
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              ) : (
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              )}
            </svg>
            <span className="text-sm font-medium">{toastMessage}</span>
          </div>
        </div>
      )}

      {labelsError && !labelsErrorDismissed && (
        <div className="mx-6 mt-4 px-4 py-2 bg-red-50 border border-red-200 rounded-md flex items-center justify-between">
          <span className="text-sm text-red-700">
            Nie udało się pobrać etykiet. Odśwież stronę.
          </span>
          <button
            onClick={() => setLabelsErrorDismissed(true)}
            className="text-red-500 hover:text-red-700 ml-4"
            aria-label="Zamknij"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}

      <LabelManagementPanel
        labels={labels}
        onCreateLabel={createLabel}
        onUpdateLabel={updateLabel}
        onDeleteLabel={deleteLabel}
      />

      <div className="px-6 py-4 border-b border-neutral-200">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-semibold text-neutral-900">
              Artykuły z urzędów pracy
            </h2>
            <div className="flex items-center gap-2">
              <LabelFilter
                labels={labels}
                filterMode={labelFilterMode}
                onFilterChange={setLabelFilterMode}
                totalCount={articles.length}
                filteredCount={filteredArticles.length}
              />
              {labelsLoading && (
                <span className="text-xs text-neutral-400">...</span>
              )}
            </div>
          </div>
          <div className="text-right">
            {pagination && pagination.total > 0 && (
              <div className="space-y-1">
                <div className="text-sm text-neutral-600">
                  artykułów:{" "}
                  {(pagination.currentPage - 1) * pagination.limit + 1}-
                  {Math.min(
                    pagination.currentPage * pagination.limit,
                    pagination.total
                  )}{" "}
                  z {pagination.total}
                </div>
                <div className="flex items-center space-x-2 justify-end">
                  <span className="text-sm text-neutral-600">Pokaż:</span>
                  <select
                    value={pagination.limit}
                    onChange={(e) => onLimitChange?.(parseInt(e.target.value))}
                    className="text-sm border border-neutral-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={500}>500</option>
                  </select>
                  <span className="text-sm text-neutral-600">na stronę</span>
                </div>
              </div>
            )}
            {!pagination && (
              <span className="text-sm text-neutral-500">
                Znaleziono {total} artykułów
              </span>
            )}
          </div>
        </div>
      </div>

      <KfsArticlesTable
        articles={filteredArticles}
        onSortChange={handleSortChange}
        currentSort={sortConfig}
        onProvinceClick={onProvinceClick}
        userRole={userRole}
        onReprocess={handleReprocess}
        onReport={handleReport}
        labels={labels}
        articleLabels={articleLabels}
        onSetArticleLabel={setArticleLabel}
      />

      <ReportArticleModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        onSubmit={handleReportSubmit}
        articleTitle={reportingArticle?.title || ""}
      />

      {pagination && pagination.total > 0 && pagination.totalPages > 1 && (
        <div className="px-6 py-4 bg-neutral-50 border-t border-neutral-200">
          <div className="flex justify-center">
            <div className="flex items-center space-x-1">
              {/* First and Previous */}
              <button
                onClick={() => onPageChange?.(1)}
                disabled={!pagination.hasPrevious}
                className="px-2 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Pierwsza strona"
              >
                &lt;&lt;
              </button>
              <button
                onClick={() => onPageChange?.(pagination.currentPage - 1)}
                disabled={!pagination.hasPrevious}
                className="px-2 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Poprzednia strona"
              >
                &lt;
              </button>

              {/* Page Numbers */}
              {(() => {
                const currentPage = pagination.currentPage;
                const totalPages = pagination.totalPages;
                const pages = [];

                // Zawsze pokaż pierwszą stronę
                if (currentPage > 3) {
                  pages.push(1);
                  if (currentPage > 4) {
                    pages.push("...");
                  }
                }

                // Pokaż strony wokół aktualnej
                for (
                  let i = Math.max(1, currentPage - 2);
                  i <= Math.min(totalPages, currentPage + 2);
                  i++
                ) {
                  pages.push(i);
                }

                // Zawsze pokaż ostatnią stronę
                if (currentPage < totalPages - 2) {
                  if (currentPage < totalPages - 3) {
                    pages.push("...");
                  }
                  pages.push(totalPages);
                }

                return pages.map((page, index) =>
                  page === "..." ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-2 py-1 text-sm text-neutral-400"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => onPageChange?.(page as number)}
                      className={`px-3 py-1 text-sm border rounded ${
                        page === currentPage
                          ? "bg-primary-600 text-white border-primary-600"
                          : "border-neutral-300 hover:bg-neutral-100"
                      }`}
                    >
                      {page}
                    </button>
                  )
                );
              })()}

              {/* Next and Last */}
              <button
                onClick={() => onPageChange?.(pagination.currentPage + 1)}
                disabled={!pagination.hasNext}
                className="px-2 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Następna strona"
              >
                &gt;
              </button>
              <button
                onClick={() => onPageChange?.(pagination.totalPages)}
                disabled={!pagination.hasNext}
                className="px-2 py-1 text-sm border border-neutral-300 rounded hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Ostatnia strona"
              >
                &gt;&gt;
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticlesList;
